<template>
    <v-row justify="space-between" align="center" class="mb-4">
        <h2 class="text-h5">Employee Management</h2>
        <v-btn color="primary" @click="isAddDialogOpen = true">
            + Add Employee
        </v-btn>
    </v-row>
    <!-- Add User Dialog -->
    <v-dialog v-model="isAddDialogOpen" max-width="950px">
        <v-card>
            <!-- Top Section: Title and Cancel Button -->
            <v-card-title class="d-flex justify-space-between align-center">
                <span class="text-h6">Add Employee </span>
                <v-btn icon @click="isAddDialogOpen = false">
                    <v-icon color="red">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <!-- Main Form -->
            <v-card-text>
                <v-form ref="addStudentForm" @submit.prevent="addEmployee">
                    <v-container>
                        <v-row>
                            <!-- Full Name -->
                            <v-col cols="12" md="4">
                                <v-text-field v-model="newEmployee.name" label="Full Name" outlined required
                                    hint="Enter the employee's full name" persistent-hint></v-text-field>
                            </v-col>

                            <!-- Employee ID -->
                            <v-col cols="12" md="4">
                                <v-text-field v-model="newEmployee.employee_id" label="Employee ID" outlined required
                                    hint="Enter the employee's ID" persistent-hint></v-text-field>
                            </v-col>

                            <!-- Gender -->
                            <v-col cols="12" md="4">
                                <v-select v-model="newEmployee.gender" :items="['Male', 'Female', 'Other']"
                                    label="Gender" outlined required></v-select>
                            </v-col>
                        </v-row>

                        <v-row>
                            <!-- Blood Group -->
                            <v-col cols="12" md="4">
                                <v-select v-model="newEmployee.blood_group"
                                    :items="['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-']" label="Blood Group"
                                    outlined required></v-select>
                            </v-col>

                            <!-- Category -->
                            <v-col cols="12" md="4">
                                <v-select v-model="newEmployee.category"
                                    :items="['1st Class', '2nd Class', '3rd Class']" label="Category" outlined
                                    required></v-select>
                            </v-col>

                            <!-- Appointment -->
                            <v-col cols="12" md="4">
                                <v-select v-model="newEmployee.appointment"
                                    :items="['Private', 'Temporary', 'Permanent', 'Relief', 'Grant']"
                                    label="Appointment Type" outlined required></v-select>
                            </v-col>
                        </v-row>

                        <v-row>
                            <!-- Teaching Level -->
                            <v-col cols="12" md="4">
                                <v-select v-model="newEmployee.teaching_level"
                                    :items="['Montessori', 'Primary Level', 'Lower Secondary', 'Secondary Level', 'Higher Secondary', 'Bachelor', 'Masters']"
                                    label="Teaching Level" outlined required></v-select>
                            </v-col>

                            <!-- Address -->
                            <v-col cols="12" md="4">
                                <v-text-field v-model="newEmployee.address" label="Address" outlined
                                    required></v-text-field>
                            </v-col>

                            <!-- Contact -->
                            <v-col cols="12" md="4">
                                <v-text-field v-model="newEmployee.contact" label="Contact" type="tel" outlined
                                    required></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <!-- Date of Birth -->
                            <v-col cols="12" md="4">
                                <v-text-field v-model="newEmployee.birth_date" label="Birth Date" type="date" outlined
                                    required></v-text-field>
                            </v-col>

                            <!-- Card Valid Date -->
                            <v-col cols="12" md="4">
                                <v-text-field v-model="newEmployee.card_valid_date" label="Card Valid Date" type="date"
                                    outlined></v-text-field>
                            </v-col>

                            <!-- Designation -->
                            <v-col cols="12" md="4">
                                <v-text-field v-model="newEmployee.designation" label="Designation" outlined
                                    required></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <!-- Citizenship No -->
                            <v-col cols="12" md="4">
                                <v-text-field v-model="newEmployee.citizenship_no" label="Citizenship No" outlined
                                    required></v-text-field>
                            </v-col>

                            <!-- PAN No -->
                            <v-col cols="12" md="4">
                                <v-text-field v-model="newEmployee.pan_no" label="PAN No" outlined
                                    required></v-text-field>
                            </v-col>

                            <!-- Image Upload -->
                            <v-col cols="12" md="4">
                                <v-file-input v-model="newEmployee.image" label="Upload Employee Image" outlined
                                    accept="image/*" @change="(file) => onFileChange(file, 'image')"></v-file-input>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </v-card-text>

            <!-- Bottom Section: Save Button -->
            <v-card-actions class="d-flex justify-end">
                <v-btn class="save-button" dark elevation="2" rounded @click="addEmployee">
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import Swal from "sweetalert2";
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { addEmployeeView, schoolDetail } from "@/services/apiServices";
import { useSchoolStore } from "@/Store/className.js";
// import { emit } from "process";
const error = ref("");

const route = useRoute();
const emit = defineEmits(["refresh-employee-list"])


const classIdFromRoute = parseInt(route.params.id);
const classInfoList = JSON.parse(localStorage.getItem("classNamesList"));

const schoolInfoList = JSON.parse(localStorage.getItem("schoolDetails"));
const schoolId = schoolInfoList?.id;

const className = classInfoList?.find(
    (item) => item.id === classIdFromRoute
)?.name;

const isAddDialogOpen = ref(false);

const newEmployee = ref({
    name: "",
    employee_id: "",
    gender: "",
    blood_group: "",
    category: "",
    appointment: "",
    teaching_level: "",
    image: null,
    address: "",
    contact: "",
    birth_date: "",
    card_valid_date: "",
    designation: "",
    citizenship_no: "",
    pan_no: "",
    school: [schoolId], // Use the school ID from your route or logic
    class_info: [classIdFromRoute], 
    class_name: className,
    roll_no: "",
    parent_name: ""
});



const mapToSmallValue = (key, value) => {
  const mapping = {
    gender: {
      Male: "male",
      Female: "female",
      Others: "others",
    },
    blood_group: {
      "O+": "o+",
      "O-": "o-",
      "A+": "a+",
      "A-": "a-",
      "B+": "b+",
      "B-": "b-",
      "AB+": "ab+",
      "AB-": "ab-",
    },
    appointment: {
      Private: "private",
      Temporary: "temporary",
      Permanent: "permanent",
      Relief: "relief",
      Grant: "grant",
    },
    category: {
      "1st Class": "1st",
      "2nd Class": "2nd",
      "3rd Class": "3rd",
    },
    teaching_level: {
      Montessori: "montessori",
      "Primary Level": "primary",
      "Lower Secondary": "lower_secondary",
      "Secondary Level": "secondary",
      "Higher Secondary": "higher_secondary",
      Bachelor: "bachelor",
      Masters: "master",
    },
  };

  return mapping[key]?.[value] || value; // Fallback to the value itself if no mapping exists
};


const fetchSchool = async () => {
    try {
        const data = await schoolDetail();
        if (data && data.id) {
            newEmployee.value.school = [data.id];
        }
    } catch (err) {
        error.value =
            err.response?.data?.message || "Failed to load school details";
    }
};

const onFileChange = (event, type) => {
    const fileList = event.target.files;

    if (fileList && fileList.length > 0) {
        const file = fileList[0];

        if (file instanceof File) {
            newEmployee.value[type] = file;
        } else {
            console.error("Invalid file type:", file);
        }
    } else {
        console.error("No file selected or invalid FileList:", fileList);
    }
};;


const addEmployee = async () => {
    const formData = new FormData();
    const addingEmployee = { ...newEmployee.value };

    ['gender', 'blood_group', 'appointment', 'category', 'teaching_level'].forEach(field => {
        addingEmployee[field] = mapToSmallValue(field, addingEmployee[field]);
    });

    // Append non-image fields to formData
    Object.entries(addingEmployee).forEach(([key, value]) => {
        if (key !== "image") formData.append(key, value);
    });

    if (newEmployee.value.image instanceof File) {
        formData.append("image", newEmployee.value.image);
    } else {
        console.error("No valid image file selected");
    }

    try {
        const response = await addEmployeeView(formData);
        emit("refresh-employee-list", response);
        // Swal.fire("Success", "Employee added successfully!", "success");
        isAddDialogOpen.value = false;

        // Reset fields except "class_info" and "school"
        Object.keys(newEmployee.value).forEach(key => {
            if (!["class_info", "school"].includes(key)) {
                newEmployee.value[key] = Array.isArray(newEmployee.value[key]) ? [] : "";
            }
        });
        Swal.fire({
        icon: "success",
        title: "Employee added successfully",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.onmouseenter = Swal.stopTimer;
          toast.onmouseleave = Swal.resumeTimer;
        },
      });
    } catch (err) {
        console.error("Error adding employee:", err);
        Swal.fire("Error", "Failed to add employee.", "error");
    }
};





onMounted(fetchSchool);
</script>

<style scoped>
.save-button {
    background: linear-gradient(90deg, #4caf50, #43a047);
    color: white;
    font-weight: bold;
    padding: 10px 20px;
    border-radius: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}
</style>